import { Send } from 'lucide-react';
import { Dispatch, SetStateAction, useContext, useEffect, useRef } from 'react';
import HumanHandover from './human-handover';
import { type ChatMutationType } from '../chat-window';
import DivWithInert from '../../div-with-inert';
import FoundationInput from '../../input/input';
import React from 'react';
import { ConfigContext } from '@/config/config-context';
import { cn } from '@/lib/utils';

type ChatWindowFooterType = {
	chatWindowEnd: boolean;
	humanHandover: boolean;
	setHumanHandover: Dispatch<SetStateAction<boolean>>;
	setInputValue: Dispatch<SetStateAction<string | undefined>>;
	inputValue: string | undefined;
	isDisabled: boolean;
	conversationLength: number;
	mutation: ChatMutationType;
};

/**
 * ChatWindowFooter component renders the footer of the chat window.
 *
 * This component handles user input and displays either the HumanHandover component
 * or the input field for the chat. It also includes a keydown event handler to submit
 * the input when the "Enter" key is pressed.
 *
 * @param {object} props - The properties object.
 * @param {boolean} props.chatWindowEnd - A state indicating if the chat window has ended.
 * @param {boolean} props.humanHandover - A state indicating if the chat has been handed over to a human.
 * @param {Dispatch<SetStateAction<boolean>>} props.setHumanHandover - A function to update the human handover state.
 * @param {Dispatch<SetStateAction<string | undefined>>} props.setInputValue - A function to update the input value state.
 * @param {string | undefined} props.inputValue - The current value of the input field.
 * @param {UseMutationResult<ConversationType | null, Error, string>} props.mutation - A mutation to create a new chat bubble with the input value.
 * @param {boolean} props.isDisabled - A state indicating if the Input and Button should be disabled.
 * @param {number} props.conversationLength - A state indicating the conversation.length used to enable the possibility for focus on input.
 * @returns {JSX.Element} The ChatWindowFooter component.
 */
const ChatWindowFooter = React.forwardRef<HTMLDivElement, ChatWindowFooterType>(
	(
		{
			chatWindowEnd,
			humanHandover,
			setHumanHandover,
			setInputValue,
			inputValue,
			isDisabled,
			conversationLength,
			mutation,
		},
		ref
	): JSX.Element => {
		const config = useContext(ConfigContext);
		const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				mutation.mutate(inputValue ?? '');
			}
		};
		const inputRef = useRef<HTMLInputElement | null>(null);

		useEffect(() => {
			if (
				!isDisabled &&
				conversationLength > 1 &&
				!humanHandover &&
				inputRef.current
			) {
				inputRef.current.focus();
			}
		}, [isDisabled, conversationLength, humanHandover]);

		return (
			<div
				className='wlcb-w-full'
				ref={ref}
			>
				<DivWithInert
					inert={chatWindowEnd}
					className='wlcb-p-space-100 wlcb-bg-white wlcb-border-t-[1px] wlcb-border-stroke-subtle wlcb-w-full wlcb-rounded-b-sm'
				>
					{humanHandover ? (
						<HumanHandover
							humanHandoverUrl={config?.humanHandoverUrl}
							setHumanHandover={setHumanHandover}
						/>
					) : (
						<FoundationInput
							ref={inputRef}
							disabled={isDisabled}
							setInputState={setInputValue}
							inputState={inputValue}
							className='!wlcb-max-w-none'
							id='ai-chat-input'
							hint='Vår AI kan göra misstag. Tänk på att dubbelkolla viktig information.'
							aria-placeholder='Skicka meddelande'
							placeholder='Skicka meddelande...'
							type='text'
							onKeyDown={handleKeyDown}
							button={{
								className: cn('', {
									'!wlcb-bg-theme-brand-secondary hover:!wlcb-bg-theme-brand-secondary-hover':
										config?.theme?.secondary !== undefined &&
										!config?.theme?.primary &&
										!inputValue,
									'!wlcb-bg-theme-brand-secondary-text-color hover:!wlcb-bg-theme-brand-secondary-hover':
										config?.theme?.secondary !== undefined &&
										!config?.theme?.primary &&
										inputValue,
									'!wlcb-bg-theme-brand-primary-text-color hover:!wlcb-bg-surface-hover':
										config?.theme?.primary && !inputValue,
									'!wlcb-bg-theme-brand-primary hover:!wlcb-bg-theme-brand-primary-hover':
										config?.theme?.primary && inputValue,
									'!wlcb-bg-secondary hover:!wlcb-bg-secondary-hover':
										!config?.theme?.secondary && !inputValue,
									'!wlcb-bg-primary hover:!wlcb-bg-primary-hover':
										!config?.theme?.primary &&
										inputValue &&
										inputValue.length > 0,
								}),
								onClick: () => mutation.mutate(inputValue ?? ''),
								'aria-label': 'Skicka meddelande till ChatBot',
								orientation: 'right',
								size: 'md',
								type: 'button',
								variant: 'secondary',
								icon: {
									iconType: (
										<Send
											aria-hidden='true'
											className={cn('wlcb-size-4', {
												'wlcb-stroke-[var(--theme-brand-secondary)]':
													inputValue &&
													config?.theme?.secondary &&
													!config?.theme?.primary,
												'wlcb-stroke-[var(--theme-brand-secondary-text-color)]':
													!inputValue &&
													config?.theme?.secondary &&
													!config?.theme?.primary,
												'wlcb-stroke-[var(--theme-brand-primary-text-color)]':
													inputValue && config?.theme?.primary,
												'wlcb-stroke-[var(--theme-brand-primary)]':
													!inputValue && config?.theme?.primary,
												'wlcb-stroke-white': !config?.theme && inputValue,
												'wlcb-stroke-black': !config?.theme && !inputValue,
											})}
										/>
									),
									orientation: 'default',
								},
							}}
						/>
					)}
				</DivWithInert>
			</div>
		);
	}
);

ChatWindowFooter.displayName = 'ChatWindowFooter';

export default ChatWindowFooter;
